/**
 * Dashboard Service
 * 
 * Handles all dashboard and statistics related API calls
 */

import apiClient, { API_ENDPOINTS } from './api';
import { handleApiError } from '../utils/apiErrorHandler';

const dashboardService = {
  /**
   * Get general dashboard statistics
   * @param {Object} params - Query parameters (date range, etc.)
   * @returns {Promise<Object>} Dashboard statistics
   */
  async getStats(params = {}) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.dashboard.stats, { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch dashboard statistics' });
    }
  },

  /**
   * Get CVO-specific dashboard statistics
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} CVO dashboard statistics
   */
  async getCVOStats(params = {}) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.dashboard.cvoStats, { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch CVO statistics' });
    }
  },

  /**
   * Get user dashboard analytics
   * @returns {Promise<Object>} User dashboard analytics data
   */
  async getUserDashboardAnalytics() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.dashboard.userDashboardAnalytics);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch user dashboard analytics' });
    }
  },

  /**
   * Get CVO dashboard data
   * @returns {Promise<Object>} CVO dashboard data
   */
  async getCVODashboard() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.dashboard.cvoDashboard);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch CVO dashboard' });
    }
  },

  /**
   * Get Authority-specific dashboard statistics
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Authority dashboard statistics
   */
  async getAuthorityStats(params = {}) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.dashboard.authorityStats, { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch Authority statistics' });
    }
  },

  /**
   * Get Authority dashboard data
   * @returns {Promise<Object>} Authority dashboard data
   */
  async getAuthorityDashboard() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.dashboard.authorityDashboard);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch Authority dashboard' });
    }
  },

  /**
   * Get recent activity feed
   * @param {Object} params - Query parameters (limit, offset, etc.)
   * @returns {Promise<Object>} Recent activity
   */
  async getRecentActivity(params = {}) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.dashboard.recentActivity, { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch recent activity' });
    }
  },
};

export default dashboardService;

